import API from "./API";
import { useSettingsStore } from "@/store/settings";
import { storeToRefs } from "pinia";

interface SearchSpringSearchResponse {
    results: any[];
    facets: any;
    pagination: {
        totalResults: number;
        totalPages: number;
        currentPage: number;
        resultsPerPage: number;
    };
    sorting: any;
}

interface Filter {
    field: string;
    value?: string;
    multiple: string;
    high?: number;
    low?: number;
}

interface Sort {
    field: string;
    direction: string;
}

const hideB2BProducts = ()=>{
    const { is_dealer } = storeToRefs(useSettingsStore());
    // Hide b2b products from d2c users && guests
    return !is_dealer.value;
};

function parseSearchSpringResponse(response: Response): Promise<SearchSpringSearchResponse> {
    if (!response.ok) {
        // Reject with an Error — rejecting a Response becomes Sentry "Error: [object Response]"
        return Promise.reject(
            new Error(
                `SearchSpring request failed with status ${response.status}${
                    response.url ? ` for ${response.url}` : ""
                }`
            )
        );
    }
    return response.json() as Promise<SearchSpringSearchResponse>;
}

class SearchSpringAPI extends API {
    urlParams: URLSearchParams;
    baseParams: string;
    adapterType: string;
    siteId: string;
    constructor(params?: { baseUrl?: string; adapter?: string, siteId?: string }) {
        const resultFormat = "json";
        let siteId = params?.siteId ?? "xxkp6b";
        const baseUrl =
            params?.baseUrl ?? `https://${siteId}.a.searchspring.io`;
        const adapter = params?.adapter ?? "fetch";
        super(baseUrl, adapter);
        this.siteId = params?.siteId ?? "xxkp6b";
        this.adapterType = adapter;
        this.baseParams = `siteId=${siteId}&resultsFormat=${resultFormat}&resultsPerPage=24&page=1`;
        this.urlParams = new URLSearchParams(this.baseParams);
    }

    appendCookies() {
        if (this.urlParams.has("userId")) {
            this.urlParams.delete("userId");
        }
        if (this.urlParams.has("sessionId")) {
            this.urlParams.delete("sessionId");
        }

        let cookies = document.cookie.split(";");
        for (let cookie of cookies) {
            let [name, value] = cookie.split("=");
            if (name.trim() == "ssUserId") {
                this.urlParams.append("userId", value.trim());
            }
            if (name.trim() == "ssSessionIdNamespace") {
                this.urlParams.append("sessionId", value.trim());
            }
        }
    }
    toBoost(){
        this.baseUrl = this.originalUrl + `/boost/${this.siteId}/recommend`;
    }
    toSearch() {
        this.baseUrl = this.originalUrl + "/api/search/search.json";
    }
    appendFilters(filters: Filter[], urlParams: URLSearchParams = this.urlParams) {
        if (filters === undefined) return;
        filters = filters ?? [];

        let params = new URLSearchParams(urlParams);
        params.forEach((value, key) => {
            if (key.startsWith("filter")) urlParams.delete(key);
        });

        for (const filter of filters) {
            if (filter.low || filter.high) {
                if (filter.low) {
                    if (urlParams.has(`filter.${filter.field}.low`)) {
                        urlParams.delete(`filter.${filter.field}.low`);
                    }
                    urlParams.append(
                        `filter.${filter.field}.low`,
                        filter.low.toString()
                    );
                }
                if (filter.high) {
                    if (urlParams.has(`filter.${filter.field}.high`)) {
                        urlParams.delete(`filter.${filter.field}.high`);
                    }
                    urlParams.append(
                        `filter.${filter.field}.high`,
                        filter.high.toString()
                    );
                }
            } else {
                if (
                    urlParams.has(`filter.${filter.field}`) &&
                    filter.multiple == "single"
                ) {
                    urlParams.delete(`filter.${filter.field}`);
                }
                urlParams.append(`filter.${filter.field}`, filter.value);
            }
        }
    }

    appendSorts(sorts: Sort[]) {
        // delete existing sorts to sort only 1 field at a time
        for (const [key, value] of this.urlParams.entries()) {
            if (key.includes('sort')) {
                this.urlParams.delete(key);
            }
        }

        sorts = sorts ?? [];
        for (const sort of sorts) {
            if (sort.direction !== "asc" && sort.direction !== "desc") {
                throw new Error("Sort direction must be either asc or desc");
            }
        }

        for (const sort of sorts) {
            if (this.urlParams.has(`sort.${sort.field}`)) {
                this.urlParams.delete(`sort.${sort.field}`);
            }
            this.urlParams.append(`sort.${sort.field}`, `${sort.direction}`);
        }
    }

    categorySearch({
        category,
        filters,
        sorts,
        page
    }: {
        category: any;
        filters?: Filter[];
        sorts?: Sort[];
        page?: number;
    }) {
        this.toSearch();
        let categoryParam = category.category_name;
        while (category.parent) {
            categoryParam = category.parent.category_name + ">" + categoryParam;
            category = category.parent;
        }
        if (this.urlParams.has("bgfilter.ss_category_hierarchy")) {
            this.urlParams.delete("bgfilter.ss_category_hierarchy");
        }
        this.urlParams.append("bgfilter.ss_category_hierarchy", categoryParam);

        this.appendFilters(filters);
        this.appendSorts(sorts);
        this.appendUrlParams();        

        if (page) {
            if (this.urlParams.has("page")) {
                this.urlParams.delete("page");
            }
            this.urlParams.append("page", page.toString());
        }

        if(hideB2BProducts()){
            this.urlParams.append('filter.b2b_product','false');
        }
        return this.get("?" + this.urlParams.toString(), {
            headers: {
            accept: "application/json"
            }
        }).then(parseSearchSpringResponse);
    }
    campaign({ tag, filters }: { tag: string, filters?: Filter[] }) {
        this.toSearch();
        if (this.urlParams.has("tag")) {
            this.urlParams.delete("tag");
        }
        this.urlParams.append("tag", tag);

        if(filters){
            this.appendFilters(filters);
        }

        if(hideB2BProducts()){
            this.urlParams.append('filter.b2b_product','false');
        }
        return this.get("?" + this.urlParams.toString(), {
            headers: {
                accept: "application/json"
            }
        }).then(parseSearchSpringResponse);
    }
    appendUrlParams(){
        if (typeof window !== "undefined" && window.location && window.location.search) {
            // get url params from Url
            const urlParams = new URLSearchParams(window.location.search);
            // foreach url param that starts with ss, append it as a bgfilter

            urlParams.forEach((value, key) => {
                if (key.startsWith("ss")) {
                    key = key.replace(/^ss|\[|\]/g, "");
                    if(this.urlParams.has(`filter.${key}`)){
                        this.urlParams.delete(`filter.${key}`);
                    }

                    let values = value.split(",")
                    for(let val of values){
                        this.urlParams.append(`filter.${key}`, val);
                    }
                }
            });
        }
    }
    search({
        searchTerm,
        filters,
        sorts,
        page,
        tag
    }: {
        searchTerm?: string;
        filters?: Filter[];
        sorts?: Sort[];
        page?: number;
        tag?: string;
    }) {
        this.toSearch();
        if (this.urlParams.has("q")) {
            this.urlParams.delete("q");
        }
        if(searchTerm) {
            if (searchTerm.length > 256) {
                throw new Error("Search term is too long");
            }
            
            this.urlParams.append("q", searchTerm);
        }
        if(tag){
            if(this.urlParams.has("tag")){
                this.urlParams.delete("tag");
            }
            this.urlParams.append("tag", tag);
        }

        
        this.appendFilters(filters);
        this.appendSorts(sorts);
        this.appendUrlParams();

        if (page) {
            if (this.urlParams.has("page")) {
                this.urlParams.delete("page");
            }
            this.urlParams.append("page", page.toString());
        }

        let result = undefined;
        if (this.adapterType == "mock") {
            if (searchTerm == "") {
                result = {
                    results: []
                };
            } else {
                result = {
                    results: [
                        {
                            id: 0,
                            name: "mocked result",
                            thumbnailImageUrl: "https://via.placeholder.com/150"
                        }
                    ]
                };
            }
        }
        if(hideB2BProducts()){
            this.urlParams.append('filter.b2b_product','false');
        }
        return this.get("?" + this.urlParams.toString(), result).then(
            parseSearchSpringResponse
        );
    }
    autocomplete({
        searchTerm,
        resultsCount
    }: {
        searchTerm: string;
        resultsCount?: number;
    }) {
        this.baseUrl = this.originalUrl + "/api/search/autocomplete.json";
        resultsCount = resultsCount ?? 4;
        if (searchTerm.length > 256) {
            throw new Error("Search term is too long");
        }
        if (this.urlParams.has("q")) {
            this.urlParams.delete("q");
        }
        this.urlParams.append("q", searchTerm);

        if (this.urlParams.has("resultsPerPage")) {
            this.urlParams.delete("resultsPerPage");
        }
        this.urlParams.append("resultsPerPage", resultsCount.toString());

        this.appendCookies();

        if (this.urlParams.has("domain")) {
            this.urlParams.delete("domain");
        }
        this.urlParams.append("domain", window.location.href);

        if(hideB2BProducts()){
            this.urlParams.append('filter.b2b_product','false');
        }
        return this.get(
            "?" + this.urlParams.toString(),
            this.adapterType == "mock"
                ? {
                      results: [
                          {
                              id: 0,
                              name: "mocked result",
                              thumbnailImageUrl:
                                  "https://via.placeholder.com/150"
                          }
                      ]
                  }
                : undefined
        ).then(parseSearchSpringResponse);
    }

    trendingSearches() {
        this.baseUrl = this.originalUrl + "/api/suggest/trending";
        const urlParams = new URLSearchParams();
        // add siteId
        urlParams.append("siteId", "xxkp6b");
        urlParams.append("limit", 5);

        if(hideB2BProducts()){
            this.urlParams.append('filter.b2b_product','false');
        }
        return this.get(
            "?" + urlParams.toString(),
            this.adapterType == "mock"
                ? {
                      trending: {
                          queries: [
                              {
                                  searchQuery: "mocked query"
                              }
                          ]
                      }
                  }
                : undefined
        ).then(parseSearchSpringResponse);
    }

    suggestions({
        searchTerm,
        productCount = 4,
        suggestionCount = 6
    }: {
        searchTerm: string;
        productCount?: number;
        suggestionCount?: number;
    }) {
        productCount = productCount ?? 4;
        suggestionCount = suggestionCount ?? 6;
        this.baseUrl = this.originalUrl + "/api/suggest/query";
        if (searchTerm.length > 256) {
            throw new Error("Search term is too long");
        }

        let urlParams = new URLSearchParams();
        // add siteId
        urlParams.append("siteId", "xxkp6b");

        if (urlParams.has("q")) {
            urlParams.delete("q");
        }
        urlParams.append("q", searchTerm);

        if (urlParams.has("productCount")) {
            urlParams.delete("productCount");
        }
        urlParams.append("productCount", productCount.toString());

        if (urlParams.has("suggestionCount")) {
            urlParams.delete("suggestionCount");
        }
        urlParams.append("suggestionCount", suggestionCount.toString());

        if(hideB2BProducts()){
            this.urlParams.append('filter.b2b_product','false');
        }
        return this.get("?" + urlParams.toString(), {
            headers: {
                accept: "application/json"
            }
        }).then(parseSearchSpringResponse);
    }

    setBgFilter({ filter, value }: { filter: string; value: string }) {
        if (this.urlParams.has(filter)) {
            this.urlParams.delete(filter);
        }
        this.urlParams.append(filter, value);
    }

    appendViewedProducts(urlParams?: URLSearchParams){
        if(typeof document === "undefined"){
            return;
        }
        if(!urlParams){
            urlParams = this.urlParams
        }
        let viewedProductsString = document.cookie
            .split("; ")
            .find((row) => row.startsWith("ssViewedProducts=")) ?? "ssViewedProducts=";
        let viewedProducts = viewedProductsString.split('=')[1]
        if(viewedProducts){
            viewedProducts = viewedProducts?.replaceAll?.("%2C", ",");
            if(urlParams.has("lastViewed")){
                urlParams.delete("lastViewed");
            }
            urlParams.append("lastViewed", viewedProducts);
        }
    }

    boost(options){
        let { tags, products,  limit} = options;

        this.toBoost();
        let urlParams = new URLSearchParams();

        urlParams.set("tags", tags.join(","));
        urlParams.set("products", products?.join?.(",") ?? []);
        urlParams.set("limit", limit.toString());
        
        if(options.shopperId){
            urlParams.set("shopperId", options.shopperId);
        }
        if(options.cart){
            urlParams.set("cart", options.cart.join(","));
        }

        if(options.categories){
            urlParams.set("categories", options.categories.join(","));
        }

        if(options.filters){
            this.appendFilters(options.filters, urlParams);
        }

        this.appendViewedProducts(urlParams);

        if(hideB2BProducts()){
            urlParams.append('filter.b2b_product','false');
        }
        return this.get("?" + urlParams.toString(), {
            headers: {
                accept: "application/json"
            }
        }).then(parseSearchSpringResponse).catch(() => {
            return [] as unknown as SearchSpringSearchResponse;
        });
    }
}

export default SearchSpringAPI;
